/** Host data for one implementation. The package accepts no organization-class selector. */
export interface FundraisersFlavor {
  readPermission: string;
  writePermission: string;
  recipientMode: 'self' | 'chosen';
  createDescription: string;
  /**
   * Whether the Fundraisers page carries the "Started by supporters" tab (FCM-WEB3) — a presentation
   * fact like `recipientMode`. A host whose organizations are fundraisers' recipients sets it from its
   * reveal knob; a host that never sets it keeps the page exactly as it was, filter tabs and all.
   */
  supporterFundraisers?: boolean;
  /**
   * Who a campaign's "Members only" admits, in the host's words (FCM-WEB33; the company's "your
   * employees and admins"), for that choice's line. A host whose organizations hold no member roster
   * leaves it unset: the server never offers that choice there (`join_policy_options`, the OQ-2 ruling).
   */
  joinMembers?: string;
  /**
   * The line above the Donors tab's list (FCM-WEB49, D-FCM-55's decision 4), in the host class's own
   * words: who the list names follows the owner's row on the server, so the words come with the host.
   * Every host sets it, so each class chooses its words.
   */
  donorsNote: string;
  /**
   * The line above the Messages tab's composer (FCM-WEB50, D-FCM-64's decision 6), in the host class's
   * own words: who a message reaches follows the owner's row on the server (a charity's reaches only the
   * donors who shared with it, D-FCM-43), so the words come with the host. Every host sets it.
   */
  messagesNote: string;
  /**
   * The line over Givmo's two notes under the composer's preview (D-FCM-40's notes, decision 2), for a
   * host some of whose donors the list doesn't name, so they read one of the notes. A host whose messages
   * reach only donors it names (the charity's) sets none, and the preview lists no notes.
   */
  messagesNotesLine?: string;
  /**
   * The permission string of the backend's `MANAGE_CAMPAIGNS` (the module's Owner tier), which schedules,
   * cancels and changes a scheduled campaign email (EMC-BE2). Unset, the Emails tab offers those acts to
   * anyone who writes and the server's refusal answers the rest.
   */
  managePermission?: string;
}

/**
 * The organization QR design editor's reveal (FCM-WEB4). Present ⇒ the share panel offers
 * "Customize" and the QR design page serves; absent ⇒ neither exists. The words are the host's.
 */
export interface FundraisersQrDesign {
  /** Beneath Save: what saving the organization's default changes. */
  saveScope: string;
  /** For a person who cannot save it: who can. Absent ⇒ nothing is said. */
  saveUnavailable?: string;
}

/**
 * Where "Donate to this fundraiser" goes (the company host's Donations › Give, EP-MNT6). `inApp` is the
 * HOST's answer — the destination is a page of the console the person is standing in — never one
 * this package derives from the href (the app grid's rule, `ModuleGrid`'s `inApp`): a plain click then
 * hops through `onNavigate` instead of reloading the console (FCM-WEB29), and the anchor keeps its
 * `href` for copy-address, a new tab and a middle-click. An external destination (the employer
 * portal's own host) stays an ordinary link.
 */
export interface FundraiserGiveLink {
  href: string;
  inApp: boolean;
}

export interface FundraisersModuleConfig {
  /** The only organization scope. Null until the host's existing read is ready. */
  orgRef: string | null;
  orgName: string;
  flavor: FundraisersFlavor;
  /** Effective permissions from the host's ONE standing read; never fetched here. */
  permissions: readonly string[] | null;
  /**
   * The class-independent capabilities off the same standing read (`null` while not reported) —
   * the QR design editor's Save reads `CAPABILITY_MANAGE_ORG_QR` here, never a class string.
   */
  capabilities?: readonly string[] | null;
  /** The QR design editor's reveal and words; absent while the host's knob is off. */
  qrDesign?: FundraisersQrDesign | null;
  /**
   * The Fundraiser Creation Wizard's reveal (EMC-WEB7): New fundraiser opens the full-window, one
   * question a screen wizard instead of the one-page create form. Absent or false while the host's
   * knob is off, and the page is exactly today's.
   */
  creationWizard?: boolean;
  /**
   * Campaign groups' reveal: the creation wizard asks a campaign "Will people raise in groups?". Groups
   * aren't built, so "Yes" only says they are coming and returns to "No". Absent or false while the
   * host's knob is off, and a campaign's path skips the question (seven steps, not eight).
   */
  campaignGroups?: boolean;
  /**
   * The signed-in person's own account email, from the host's identity; never fetched here. The
   * organization contact offer prefills it on a row that holds no address yet, and accepts it as
   * the person's own. Absent where no offer can appear.
   */
  viewerEmail?: string | null;
  /**
   * EMC-WEB4: the organization has Board switched on, so a campaign offers "Create Board Ask"
   * (Board owns the ask; the page answers its own not-available where the backend withholds it).
   */
  boardAsk?: boolean;
  /**
   * Whether this person sees the CRM's contacts, for an email's audience picker (D-EMC-10): without them
   * the picker rented from `@givmo/crm` shows names and counts with its line about access, and the host's
   * request door. Absent, the picker shows no such line.
   */
  crmAccess?: {
    contactsVisible: boolean;
    onRequestAccess?: () => void;
    accessRequestPending?: boolean;
  };
  /**
   * The AI email designer's assistant (EMC-WEB10, D-EMC-20 (a)): what its thread needs from the host to
   * talk to Givmo's assistant (the API's base and the stream's auth headers) and whether this person may
   * turn the assistant on for the organization (advisory: the server refuses on its own). Absent or
   * null, the designer page is not served.
   */
  emailDesigner?: {
    apiBaseUrl: string;
    authHeaders: () => Promise<Record<string, string>> | Record<string, string>;
    canAcceptForOrg: boolean;
    privacyPolicyUrl: string;
  } | null;
  /**
   * The person's OTHER organizations, from the host's membership read, for a page whose fundraiser
   * isn't this organization's (a link opened while another organization is active in this tab): the
   * page asks each of them, and where one holds the fundraiser it offers to switch there instead of
   * saying "not found". Absent or empty, a missing fundraiser is simply not found.
   */
  otherOrganizations?: {
    organizations: readonly { orgRef: string; name: string }[];
    switchTo: (orgRef: string) => void;
  } | null;
  /**
   * The organization's time zone in force (an IANA name) off the host's ONE standing read: the one it
   * chose in Settings, else the one its state derives. New deadlines and emails are set in it. Absent
   * or null, they are set in the reader's own zone, named on screen.
   */
  orgTimeZone?: string | null;
  links: {
    donationsGive?: (fundraiserId: string) => FundraiserGiveLink | null;
    donateBaseUrl: string;
    /**
     * The host's organization Settings page, where its time zone is changed; a host names it only
     * where Settings lets this person change the time zone (`useOrgSettingChangeable`), and it is
     * offered only to a holder of `write_settings`. Absent, no link.
     */
    orgSettings?: string;
  };
  onNavigate: (href: string) => void;
  /** Root namespace; existing interactive ids stay stable for the moved e2e specs. */
  testIdPrefix: string;
}

export function mayWriteFundraisers(config: FundraisersModuleConfig): boolean {
  return (
    config.orgRef !== null && config.permissions?.includes(config.flavor.writePermission) === true
  );
}

/** Whether this person may schedule, cancel or change a scheduled campaign email (the Owner tier). */
export function mayManageCampaignEmails(config: FundraisersModuleConfig): boolean {
  const manage = config.flavor.managePermission;
  return manage === undefined
    ? mayWriteFundraisers(config)
    : config.orgRef !== null && config.permissions?.includes(manage) === true;
}

export function mayReadFundraisers(config: FundraisersModuleConfig): boolean {
  return (
    config.orgRef !== null &&
    (config.permissions === null || config.permissions.includes(config.flavor.readPermission))
  );
}

/** Whether a standing read's capabilities hold `write_settings` (changing the organization's settings). */
export function holdsWriteSettings(capabilities: readonly string[] | null | undefined): boolean {
  return (capabilities ?? []).some((capability) => capability.toLowerCase() === 'write_settings');
}

/** Where a person who may change the organization's settings changes its time zone, or `null`. */
export function orgSettingsHref(config: FundraisersModuleConfig): string | null {
  const href = config.links.orgSettings;
  return href && holdsWriteSettings(config.capabilities) ? href : null;
}
